<template>
  <div>
    <!-- Page Header -->
    <section class="section" style="background: white; border-bottom: 1px solid var(--color-border-light);">
      <div class="container">
        <div class="page-header">
          <div>
            <h1 class="page-title">สินค้าทั้งหมด</h1>
            <p class="page-subtitle">ค้นพบสินค้าคุณภาพในราคาที่คุ้มค่า</p>
          </div>
          
          <!-- Search and Filter -->
          <div class="filter-controls">
            <div class="search-box">
              <svg class="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input
                v-model="searchQuery"
                @input="handleSearch"
                type="text"
                placeholder="ค้นหาสินค้า..."
                class="form-input search-input"
              >
            </div>
            
            <select
              v-model="selectedCategory"
              @change="handleCategoryChange"
              class="form-input category-select"
            >
              <option value="">ทุกหมวดหมู่</option>
              <option 
                v-for="category in categories" 
                :key="category.id" 
                :value="category.slug"
              >
                {{ category.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </section>

    <!-- Products Section -->
    <section class="section">
      <div class="container">
        <!-- Results Summary -->
        <div v-if="!loading && products.length > 0" class="results-summary">
          <p class="text-muted">
            แสดงผล {{ products.length }} รายการ
            <span v-if="searchQuery">สำหรับ "{{ searchQuery }}"</span>
            <span v-if="selectedCategoryName">ในหมวดหมู่ {{ selectedCategoryName }}</span>
          </p>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center" style="padding: 3rem 0;">
          <div class="loading-spinner" style="margin: 0 auto;"></div>
          <p class="text-muted mt-4">กำลังโหลดสินค้า...</p>
        </div>

        <!-- Products Grid -->
        <div v-else-if="products.length > 0" class="products-grid">
          <div v-for="product in products" :key="product.id" class="product-card">
            <div class="product-image">
              <img 
                v-if="product.images && product.images[0]" 
                :src="getImageUrl(product.images[0].image_path)" 
                :alt="product.name"
              >
              <div v-else class="product-image-placeholder">
                <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
            </div>
            <div class="product-info">
              <p v-if="product.category" class="product-category">{{ product.category.name }}</p>
              <h3 class="product-name">{{ product.name }}</h3>
              <p class="product-desc">{{ product.description }}</p>
              <div class="product-price">฿{{ Number(product.price).toLocaleString() }}</div>
              <div class="product-actions">
                <router-link :to="`/product/${product.slug}`" class="btn btn-outline btn-sm" style="flex: 1;">
                  ดูรายละเอียด
                </router-link>
                <button @click="addToCart(product)" class="btn btn-primary btn-sm">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <svg class="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-4.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7"/>
          </svg>
          <h3 class="empty-title">ไม่พบสินค้า</h3>
          <p class="empty-desc">{{ getEmptyMessage() }}</p>
          <button v-if="hasActiveFilters" @click="handleResetFilters" class="btn btn-primary">
            ล้างตัวกรอง
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import api from '../api';
import { useCartStore } from '../stores/cart';
import { useNotification } from '../composables/useNotification';
import { getImageUrl } from '../utils/imageUrl';

const products = ref([]);
const categories = ref([]);
const loading = ref(true);
const searchQuery = ref('');
const selectedCategory = ref('');
const searchTimeout = ref(null);

const cartStore = useCartStore();
const { showSuccess, showError } = useNotification();

const selectedCategoryName = computed(() => {
  if (!selectedCategory.value) return '';
  const category = categories.value.find(cat => cat.slug === selectedCategory.value);
  return category?.name || '';
});

const hasActiveFilters = computed(() => {
  return searchQuery.value || selectedCategory.value;
});

const fetchProducts = async () => {
  loading.value = true;
  try {
    const params = {};
    if (searchQuery.value) params.search = searchQuery.value;
    if (selectedCategory.value) params.category = selectedCategory.value;
    
    const response = await api.get('/products', { params });
    products.value = response.data.data;
  } catch (error) {
    console.error('Failed to fetch products:', error);
    products.value = [];
  } finally {
    loading.value = false;
  }
};

const fetchCategories = async () => {
  try {
    const response = await api.get('/categories');
    categories.value = response.data.data;
  } catch (error) {
    console.error('Failed to fetch categories:', error);
  }
};

const handleSearch = () => {
  if (searchTimeout.value) clearTimeout(searchTimeout.value);
  searchTimeout.value = setTimeout(() => fetchProducts(), 500);
};

const handleCategoryChange = () => {
  fetchProducts();
};

const handleResetFilters = () => {
  searchQuery.value = '';
  selectedCategory.value = '';
  fetchProducts();
};

const getEmptyMessage = () => {
  if (searchQuery.value && selectedCategory.value) {
    return `ไม่พบสินค้าที่ค้นหา "${searchQuery.value}" ในหมวดหมู่ ${selectedCategoryName.value}`;
  } else if (searchQuery.value) {
    return `ไม่พบสินค้าที่ค้นหา "${searchQuery.value}"`;
  } else if (selectedCategory.value) {
    return `ไม่มีสินค้าในหมวดหมู่ ${selectedCategoryName.value}`;
  }
  return 'ไม่มีสินค้า';
};

const addToCart = async (product) => {
  try {
    await cartStore.addToCart(product.id, null, 1);
    showSuccess(`เพิ่ม "${product.name}" ลงตะกร้าแล้ว`);
  } catch (error) {
    console.error('Add to cart error:', error);
    showError('ไม่สามารถเพิ่มสินค้าได้');
  }
};

onMounted(async () => {
  await Promise.all([fetchCategories(), fetchProducts()]);
});
</script>

<style scoped>
.page-header {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .page-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.25rem;
}

.page-subtitle {
  font-size: 1rem;
  color: var(--color-text-light);
}

.filter-controls {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

@media (min-width: 640px) {
  .filter-controls {
    flex-direction: row;
    width: auto;
  }
}

.search-box {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: var(--color-text-muted);
}

.search-input {
  padding-left: 2.5rem;
  width: 100%;
}

@media (min-width: 640px) {
  .search-input {
    width: 240px;
  }
}

.category-select {
  width: 100%;
}

@media (min-width: 640px) {
  .category-select {
    width: auto;
    min-width: 160px;
  }
}

.results-summary {
  margin-bottom: 1.5rem;
}

.product-category {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

.product-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #d1d5db;
}

.empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.empty-icon {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  color: var(--color-text-muted);
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.empty-desc {
  color: var(--color-text-light);
  margin-bottom: 1.5rem;
}
</style>
